<template>
  <div class="messenger-pre-chat-block">
    <div
      v-if="formStep === 'message'"
      class="pre-chat-message-step"
    >
      <widget-form-line :label="preChatViewSettings.messageLabel">
        <widget-form-field-textarea
          name="message"
          :placeholder="$t('messengerWidget.messageField.placeholder')"
          :max-length="messengerMessageMaxLength"
          :size="messageFieldHeight"
          :autofocus="fieldAutofocus === 'message'"
          @keydown.enter.exact.prevent="goToFormStep('introduction')"
        />
      </widget-form-line>

      <widget-button
        v-bind="themeButtonProps"
        class="w100pr mt-3"
        @click="goToFormStep('introduction')"
      >
        <messenger-text-with-emoji :text="preChatViewSettings.messageButtonText" />
      </widget-button>
    </div>

    <div
      v-else-if="formStep === 'introduction'"
      class="pre-chat-introduction-step"
    >
      <div class="pre-chat-introduction-step__title">
        <messenger-text-with-emoji
          :text="preChatViewSettings.introductionTitle"
          emoji-size="20px"
        />
      </div>

      <div
        class="pre-chat-introduction-step__fields"
        @keydown.enter.exact.capture.prevent="$emit('submit')"
      >
        <widget-form-line
          :label="$t('messengerWidget.nameField.label')"
          :required="fieldMap['name'].isRequired"
          :style="{ order: fieldMap['name'].order }"
        >
          <widget-form-field-text
            ref="nameFieldRef"
            name="name"
            :placeholder="$t('messengerWidget.nameField.placeholder')"
            :autofocus="fieldAutofocus === 'name'"
            :tabindex="fieldMap['name'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.emailField.label')"
          :required="fieldMap['email'].isRequired"
          :style="{ order: fieldMap['email'].order }"
        >
          <widget-form-field-text
            ref="emailFieldRef"
            name="email"
            :placeholder="$t('messengerWidget.emailField.placeholder')"
            :autofocus="fieldAutofocus === 'email'"
            :tabindex="fieldMap['email'].order"
          />
        </widget-form-line>
      </div>

      <widget-button
        v-bind="themeButtonProps"
        class="w100pr mt-4"
        :is-loading="isSubmitting"
        :loading-title="$t('common.sending')"
        @click="$emit('submit')"
      >
        <messenger-text-with-emoji :text="preChatViewSettings.introductionButtonText" />
      </widget-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MessengerPreChatForm, MessengerPreChatViewSettings, PreChatFormStep } from '@widgets/messenger/types'
import WidgetFormLine from '@widgets/shared/components/WidgetFormLine.vue'
import WidgetFormFieldText from '@widgets/shared/components/fields/WidgetFormFieldText.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import WidgetFormFieldTextarea from '@widgets/shared/components/fields/WidgetFormFieldTextarea.vue'
import { useFormFieldMap } from '@widgets/messenger/compositions/useFormFieldMap'
import { messengerMessageMaxLength } from '@widgets/messenger/const'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import type { WebWidgetDetailsPreChatFieldName } from '@/data/models/domain/messages/settings/webWidget/types'
import { useWidgetFormProvideInject } from '@/composition/widgetForm'
import { ref, watch } from '@/composition/vue/compositionApi'

const {
  preChatViewSettings,
  formStep = 'message',
  isSubmitting,
  messageFieldHeight = '64px',
} = defineProps<{
  preChatViewSettings: MessengerPreChatViewSettings
  formStep?: PreChatFormStep
  isSubmitting?: boolean
  messageFieldHeight?: string // 64px
}>()

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'change-form-step', step: PreChatFormStep): void
}>()

const { themeButtonProps } = useWidgetTheme()
const { fieldMap } = useFormFieldMap<WebWidgetDetailsPreChatFieldName>(() => preChatViewSettings.formFields)
const widgetForm = useWidgetFormProvideInject<MessengerPreChatForm>().inject()

const fieldAutofocus = ref<'message' | 'name' | 'email'>()

const goToFormStep = (step: PreChatFormStep) => {
  if (widgetForm && !widgetForm.validateField?.('message')) return
  emit('change-form-step', step)
}

const setFocus = (step: PreChatFormStep) => {
  if (step === 'message') {
    fieldAutofocus.value = 'message'
  }
  if (step === 'introduction') {
    const firstField = preChatViewSettings.formFields[0]
    fieldAutofocus.value = firstField.name
  }
}

watch(
  () => formStep,
  (step) => {
    setTimeout(() => {
      setFocus(step)
    }, 0)
  },
)
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.messenger-pre-chat-block {
  @include widget-block();
  padding: 16px;
}

.pre-chat-introduction-step {
  padding-top: 8px;

  &__title {
    @include truncate(3);
    color: $emphasize;
    font-size: 20px;
    font-weight: $bold-font;
    line-height: 24px;
    text-align: center;
    margin-bottom: 32px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
